/** YYYY-MM-DD in the device's local time. Avoids `toISOString().slice(0,10)`,
 *  which shifts dates one back in timezones west of UTC for late-evening
 *  picks. Right for rendering a date the viewer picked; wrong for deciding
 *  whether a departure has passed — use {@link agencyTodayIso} for that. */
export function localDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Today's `YYYY-MM-DD` on the AGENCY's calendar.
 *
 *  Departures, seasons and run periods are dates in the agency's zone; the
 *  device's zone is unrelated to them, and using it moves the boundary by up
 *  to a day in either direction.
 *
 *  Storefront: at 2026-08-23 18:45Z an agency on UTC still had a 22:00
 *  departure to sell, and a viewer in Kathmandu (already 00:30 on the 24th)
 *  saw that date greyed out. Operator dashboard: an operator in UTC+05:45
 *  administering a UTC-05:00 agency got a season floor a full day out, so a
 *  season the agency considers valid was refused.
 *
 *  `en-CA` because it formats as `YYYY-MM-DD`, so comparisons stay string
 *  compares.
 *
 *  Absent and unusable zones both degrade to the device's date rather than
 *  throwing: `Intl` raises on a bad `timeZone`, and a throw inside a render
 *  blanks the whole calendar or builder step. */
export function agencyTodayIso(
  timezone: string | undefined,
  now: Date = new Date(),
): string {
  if (!timezone) return localDateStr(now);
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return localDateStr(now);
  }
}
